import { MoreVert } from '@mui/icons-material'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import { IconButton, Menu, Slider as SliderRoot, Typography } from '@mui/material'
import { Palette } from '@mui/material/styles'
import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PartialDeep } from 'type-fest'

import { FlexBox } from 'components/Box'
import { ListItemButton } from 'components/List'
import {
  AbTestsAddEditContext,
  AbTestsAddEditContextType,
} from 'features/ab-tests/contexts/AbTestsAddEditContext'
import {
  AbTestsCommonSchema,
  Variants,
} from 'features/ab-tests/forms/AbTestsCommonForm/abTestsCommonSchema'
import { rearrangeTrafficShare } from 'features/ab-tests/forms/AbTestsCommonForm/utils'
import { useMenu } from 'hooks/useMenu'
import { theme } from 'providers/material-ui'
import { TrafficShareHeaderColumnCellProps } from './types'
import { useTrafficShare } from './useTrafficShare'

export const TrafficShareHeaderColumnCell = ({
  header,
}: TrafficShareHeaderColumnCellProps): React.JSX.Element => {
  const { anchor, close, isOpen, open } = useMenu()
  const { t } = useTranslation('features/abTests')
  const { t: tC } = useTranslation('common')

  const {
    isDisabled,
    loading: formLoading,
    setValid,
  } = useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)
  const {
    formState: { isValid },
    getValues,
    setValue,
  } = useFormContext<AbTestsCommonSchema>()
  const { variants } = getValues()

  if (variants === undefined) {
    throw new Error('Variants should be defined at this level.')
  }

  const { isTotalShareValid, totalShare } = useTrafficShare()

  const trafficShareColor: Palette['error' | 'info']['main'] =
    theme.palette[isTotalShareValid ? 'info' : 'error'].main

  useEffect(() => {
    setValid(isValid && isTotalShareValid)
  }, [totalShare, isValid])

  const handleRearrangeTrafficShare = (variants: PartialDeep<Variants>): void => {
    setValue('variants', rearrangeTrafficShare(variants), {
      shouldDirty: true,
      shouldValidate: true,
    })
    close()
  }

  const Slider = (): React.JSX.Element => (
    <FlexBox
      axis='y'
      sx={{ alignItems: 'flex-start' }}
    >
      <FlexBox axis='x'>
        <SliderRoot
          sx={{
            '.MuiSlider-thumb': {
              display: 'none',
            },
            color: trafficShareColor,
            minWidth: 200,
          }}
          value={totalShare}
        />
        <Typography sx={{ ml: 2 }}>{tC('percentage', { value: totalShare })}</Typography>
      </FlexBox>

      {!isTotalShareValid && (
        <Typography color={trafficShareColor}>
          {t('common.variants.table.trafficShare.error')}
        </Typography>
      )}
    </FlexBox>
  )

  return (
    <FlexBox axis='x'>
      {header}

      <IconButton
        data-cy='variants-traffic-share-button'
        disabled={isDisabled || formLoading}
        onClick={open}
        sx={{ mx: 1 }}
      >
        <MoreVert data-cy='more-vert-icon' />
      </IconButton>

      <Menu
        anchorEl={anchor}
        onClose={close}
        open={isOpen}
      >
        <ListItemButton
          icon={<DragHandleIcon data-cy='drag-handle-icon' />}
          onClick={(): void => void handleRearrangeTrafficShare(variants)}
          text={t('common.variants.table.trafficShare.distributeEvenly')}
        />
      </Menu>

      <Slider />
    </FlexBox>
  )
}
